import { useState } from "react";

import type {
  RunSourceRefresh,
  RunSourceRefreshArrival,
} from "../api/hooks/useStatefulAgents";
import styles from "./RunView.module.css";
import { Chevron } from "./Chevron";

type Source = NonNullable<RunSourceRefresh["sources"]>[number];

interface Row {
  label: string;
  status: Source["status"];
  detail: string;
  during: boolean;
  kind?: Source["kind"];
  newMessages?: number;
  arrivals: RunSourceRefreshArrival[];
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

// An email thread's upload is named "<subject> [<gmail thread id>].json".
const displayName = (a: RunSourceRefreshArrival, name: string) =>
  a.kind === "email" ? name.replace(/ \[[^\]]+\]\.json$/, "") : name;

const arrivalLine = (a: RunSourceRefreshArrival) => {
  const names = a.uploads.map((u) => displayName(a, u.name)).join(" · ");
  const more = a.count - a.uploads.length;
  return `${a.heading}: ${names}${more > 0 ? ` +${more} more` : ""}`;
};

// A source that did not run brought nothing in, so arrivals never sit under it.
const ran = (r: Row) => r.status !== "skipped" && r.status !== "failed";

// Inboxes (and global Drive items) that refreshed alike read as one line.
const groupOf = (s: Source, during: boolean): string | null => {
  const base = `${s.status}|${s.detail}|${during}`;
  if (s.kind === "gmail" && s.label.startsWith("Gmail inbox "))
    return `gmail|${base}`;
  if (s.kind === "drive" && s.label.startsWith("Global Drive"))
    return `global|${base}`;
  return null;
};

const rowsOf = (sources: Source[], firstDuring: number): Row[] => {
  const rows: Row[] = [];
  const grouped = new Map<string, { row: Row; members: string[] }>();
  sources.forEach((s, i) => {
    const during = i >= firstDuring;
    const key = groupOf(s, during);
    const existing = key ? grouped.get(key) : undefined;
    if (existing) {
      existing.members.push(s.label);
      return;
    }
    const row: Row = {
      label: s.label,
      status: s.status,
      detail: s.detail,
      during,
      kind: s.kind,
      newMessages: s.new_messages,
      arrivals: [],
    };
    rows.push(row);
    if (key) grouped.set(key, { row, members: [s.label] });
  });
  for (const [key, { row, members }] of grouped) {
    if (members.length < 2) continue;
    row.label = key.startsWith("gmail")
      ? `Gmail (${members.map((m) => m.replace("Gmail inbox ", "")).join(", ")})`
      : `Global Drive (${plural(members.length, "item")})`;
  }
  return rows;
};

// Mail under the inboxes, files under the project's Drive; whatever has no such row is
// listed on its own, its heading saying what it is.
const placeArrivals = (rows: Row[], arrivals: RunSourceRefreshArrival[]) => {
  const mailRow = rows.find((r) => r.kind === "gmail" && ran(r));
  const fileRow = rows.find(
    (r) => r.kind === "drive" && r.label.startsWith("Drive for") && ran(r),
  );
  const unplaced: RunSourceRefreshArrival[] = [];
  for (const a of arrivals) {
    const row = a.kind === "file" ? fileRow : mailRow;
    if (row) row.arrivals.push(a);
    else unplaced.push(a);
  }
  return unplaced;
};

const summaryOf = (refresh: RunSourceRefresh, sources: Source[]): string => {
  if (refresh.error) return "not refreshed";
  if (sources.length === 0) return "nothing to refresh";
  const arrivals = refresh.arrivals;
  if (!arrivals) {
    // Recorded before refreshes were grouped.
    return plural(refresh.new_upload_count ?? 0, "new or changed document");
  }
  const parts = arrivals.map(
    (a) => `${a.count} ${a.count === 1 ? a.one : a.many}`,
  );
  const messages = sources.reduce((n, s) => n + (s.new_messages ?? 0), 0);
  if (messages) parts.push(plural(messages, "new Slack message"));
  return parts.length ? parts.join(", ") : "nothing new";
};

// Only a claim the refresh can stand behind: the inboxes and the project's Drive are what
// its count covers; a global Drive item's files are not counted on a project's run.
const outcomeOf = (
  r: Row,
  arrivals: RunSourceRefreshArrival[] | undefined,
): string => {
  if (r.newMessages !== undefined) {
    return r.newMessages
      ? `, ${plural(r.newMessages, "new message")}`
      : ", nothing new";
  }
  if (r.status !== "refreshed" || !arrivals) return "";
  if (r.kind === "gmail" && !arrivals.some((a) => a.kind !== "file"))
    return ", nothing new";
  if (
    r.label.startsWith("Drive for") &&
    !arrivals.some((a) => a.kind === "file")
  ) {
    return ", nothing new";
  }
  return "";
};

// The refresh the turn waited for, as a footnote beside its actions: collapsed when
// every source refreshed, open when one did not. Failures are red; a source the turn
// read part-refreshed (partial, still running, skipped) is amber, never hidden.
export const SourceRefreshReport = ({
  refresh,
}: {
  refresh: RunSourceRefresh;
}) => {
  const sources = refresh.sources ?? [];
  const failed = !!refresh.error || sources.some((s) => s.status === "failed");
  const degraded = sources.some(
    (s) => s.status !== "refreshed" && s.status !== "failed",
  );
  const [open, setOpen] = useState(failed || degraded);
  // The last ``during_turn`` sources were refreshed as the turn worked.
  const rows = rowsOf(sources, sources.length - (refresh.during_turn ?? 0));
  const unplaced = placeArrivals(rows, refresh.arrivals ?? []);
  const rowClass = (status: string) =>
    status === "failed"
      ? styles.refreshFailed
      : status === "refreshed"
        ? undefined
        : styles.refreshDegraded;
  return (
    <div className={styles.turnContextReport}>
      <button
        type="button"
        className={styles.turnContextToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Chevron tree open={open} /> Context sources refreshed:{" "}
        {summaryOf(refresh, sources)}
        {failed && (
          <span className={styles.refreshFailed}> (with failures)</span>
        )}
        {!failed && degraded && (
          <span className={styles.refreshDegraded}> (incomplete)</span>
        )}
      </button>
      {open && (
        <ul className={styles.turnContextRows}>
          {refresh.error && (
            <li className={styles.refreshFailed}>{refresh.error}</li>
          )}
          {rows.map((r, i) => (
            <li key={`${r.label}-${i}`} className={rowClass(r.status)}>
              {r.label}:{" "}
              {r.status === "skipped"
                ? "not refreshed"
                : r.status.replace("_", " ")}
              {r.during ? ", as the turn worked" : ""}
              {outcomeOf(r, refresh.arrivals)}
              {r.detail ? ` (${r.detail})` : ""}
              {r.arrivals.length > 0 && (
                <ul className={styles.turnContextRows}>
                  {r.arrivals.map((a) => (
                    <li key={`${a.kind}-${a.change}`}>{arrivalLine(a)}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {unplaced.map((a) => (
            <li key={`${a.kind}-${a.change}`}>{arrivalLine(a)}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
